import { useMutation, useQuery } from "@apollo/client";
import { Editor } from "@tiptap/react";
import {
  ChangeEvent,
  FormEventHandler,
  MouseEvent,
  useRef,
  useState,
} from "react";
import {
  CreateTopicNewPageMutationMutation,
  CreateTopicNewPageMutationMutationVariables,
  GetUpcomingMeetingsQuery,
  GetUpcomingMeetingsQueryVariables,
} from "types/graphql-schema";

import createOrUpdateTopicMutation from "@apps/meeting-new/graphql/create-or-update-topic-mutation";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import getProfileOneononeQuery from "@apps/profile/overview-tab/graphql/get-profile-oneonone-query";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { Select, SelectOption } from "@components/select/select";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  getFormattedMeetingDateTimes,
} from "@helpers/helpers";

import getUpcomingMeetingsQuery from "./graphql/get-upcoming-meetings-query";

const notesToMarkdown = (notes: string | string[] | undefined): string => {
  if (!notes) return "";
  if (Array.isArray(notes)) return notes.join("\n");
  if (typeof notes === "string") return notes;
  return "";
};

const DialogLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label
    className={classNames(
      "text-gray-500 text-sm flex items-center gap-2 cursor-pointer font-medium w-24 shrink-0",
      className,
    )}
  >
    {children}
  </label>
);

const AddTopicToMeetingDialog = ({
  topicTitle,
  topicNotes,
  initialMeetingId,
  variables,
  onClose,
  onCreatedTopic,
}: {
  topicTitle: string;
  topicNotes?: string | string[];
  initialMeetingId?: number;
  variables: GetUpcomingMeetingsQueryVariables;
  onClose: () => void;
  onCreatedTopic: () => void;
}) => {
  const currentUser = currentUserVar();
  const [isShowingInvalidForm, setIsShowingInvalidForm] = useState(false);
  const initialNotesMarkdown = notesToMarkdown(topicNotes);
  const notesEditorRef = useRef<Editor | null>(null);

  const [form, setForm] = useState<{
    title: string;
    meetingId: number;
  }>({
    title: topicTitle,
    meetingId: initialMeetingId || 0,
  });

  let formInvalid = "";
  if (form.title.trim() === "") {
    formInvalid = "Enter a title";
  } else if (!form.meetingId) {
    formInvalid = "Select a meeting";
  }

  const { data, loading: loadingQuery } = useQuery<
    GetUpcomingMeetingsQuery,
    GetUpcomingMeetingsQueryVariables
  >(getUpcomingMeetingsQuery, {
    variables,

    onCompleted: (response) => {
      // Only auto-select if no initial meeting was provided
      if (!initialMeetingId) {
        const meetingId = response.meetings?.edges[0]?.node?.id;
        if (meetingId) {
          setForm((prev) => ({ ...prev, meetingId }));
        }
      }
    },
  });
  const meetings = data?.meetings ? assertEdgesNonNull(data?.meetings) : [];
  const options =
    meetings.length > 0
      ? meetings.map((meeting) => ({
          label: meeting.title,
          value: meeting.id,
          description:
            meeting.startDatetime && meeting.endDatetime
              ? `${getFormattedMeetingDateTimes(
                  meeting.startDatetime,
                  meeting.endDatetime,
                )}`
              : "Not scheduled",
        }))
      : [
          {
            label: "No meetings found",
            value: 0,
          },
        ];

  const handleClose = () => {
    onClose();
    setIsShowingInvalidForm(false);
  };

  const handleSubmitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [createTopic, { error, loading: createTopicLoading }] = useMutation<
    CreateTopicNewPageMutationMutation,
    CreateTopicNewPageMutationMutationVariables
  >(createOrUpdateTopicMutation);

  const handleClickSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (formInvalid) {
      setIsShowingInvalidForm(true);
      return;
    }

    const isFormalOneonone = variables.isFormalOneonone ?? false;
    const editor = notesEditorRef.current;
    const notesJson =
      editor && !editor.isEmpty ? JSON.stringify(editor.getJSON()) : null;

    createTopic({
      variables: {
        title: form.title,
        meetingId: form.meetingId,
        assignee: currentUser.id,
        ...(notesJson && {
          includesIndividualNotes: isFormalOneonone,
          includesSharedNotes: !isFormalOneonone,
          ...(isFormalOneonone
            ? { individualNotes: notesJson }
            : { discussionNotes: notesJson }),
        }),
      },
      refetchQueries: [getMeetingQuery, getProfileOneononeQuery],
      onCompleted: () => {
        setIsShowingInvalidForm(false);
        successNotificationVar({
          title: `Topic added to meeting.`,
          timeout: 3000,
        });
        onCreatedTopic();
      },
    });
  };

  const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, title: e.target.value }));
  };

  const handleChangeMeeting = (opt: SelectOption<number>) => {
    setForm((prev) => ({ ...prev, meetingId: opt.value }));
  };

  return (
    <Modal onClose={handleClose}>
      <form className="flex flex-col gap-6 p-6" onSubmit={handleSubmitForm}>
        <ModalTitle onClose={handleClose}>
          <div className="flex items-center gap-2">
            Add a Topic to a Meeting
          </div>
        </ModalTitle>

        <div className="flex items-center gap-4 ">
          <DialogLabel className="mb-1">Meeting</DialogLabel>
          <div className="w-full flex-1">
            {loadingQuery ? (
              <div className="flex items-center text-sm text-gray-400 gap-2 min-h-[38px]">
                <Loading mini size={4} />
                Loading meetings...
              </div>
            ) : (
              <Select<number>
                value={form.meetingId}
                disabled={loadingQuery || meetings.length === 0}
                options={options}
                onChange={handleChangeMeeting}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DialogLabel className="mb-1">Topic*</DialogLabel>
          <div className="w-full flex-1">
            <Input
              value={form.title}
              disabled={loadingQuery}
              onChange={handleChangeTitle}
            />
          </div>
        </div>

        {initialNotesMarkdown && (
          <div className="flex gap-4">
            <DialogLabel className="self-start">Notes</DialogLabel>
            <div className="w-full flex-1">
              <TextareaWysiwyg
                ref={notesEditorRef}
                value={initialNotesMarkdown}
                editable={!loadingQuery}
                placeholder="Add notes..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 text-sm">
          {formInvalid && isShowingInvalidForm && (
            <div className="text-red-700 empty:hidden mb-4">{formInvalid}</div>
          )}
          <div className="gap-4 grid grid-cols-2">
            <Button
              disabled={createTopicLoading || loadingQuery}
              type="submit"
              theme="primary"
              onClick={handleClickSubmit}
              className="flex items-center gap-2"
              size={ButtonSize.large}
            >
              {createTopicLoading ? "Adding Topic" : "Add Topic"}
              {createTopicLoading && <Loading mini size="4" color="white" />}
            </Button>
            <Button
              disabled={createTopicLoading}
              onClick={handleClose}
              type="button"
              text="Cancel"
              size={ButtonSize.large}
            />
          </div>
          {error && (
            <div className="mt-4">
              <GraphqlError
                whatDidNotWork="The topic could not be added to the meeting."
                error={error}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddTopicToMeetingDialog;
